import React, { useState, useEffect } from "react";
import styles from "./Css/OverlayHome.module.css";
import { Play, Info, BookOpen } from "lucide-react";
import Loading from "../../common/HousGlass Loader/Loading";
import { useHeroSectionsByTypePublic } from "../../hooks/herosection/useHeroSectionPublicHooks";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import { useAudioPlayer } from "../../components/audio/AudioPlayerContext";
import { useAddContinueListening } from "../../hooks/useContinueListening";
import useAuthStore from "../../store/authStore";

const OverlayHome = ({type, category}) => {
  const { heroSections, isLoading: isLoadingHeroSections } =
    useHeroSectionsByTypePublic(type);
  const [selectedSection, setSelectedSection] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  
  // ADD THESE HOOKS
  const { playAudio } = useAudioPlayer();
  const { addContinueListening } = useAddContinueListening();
  const { isAuthenticated } = useAuthStore();

  // Check if the user is on a mobile device
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkDevice();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkDevice);
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('resize', checkDevice);
    };
  }, []);

  // Update selectedSection when heroSections data is available
  useEffect(() => {
    if (heroSections && heroSections.length > 0) {
      setSelectedSection(heroSections[0]);
    }
  }, [heroSections]);

  // Get content data based on category
  const getContentData = (section) => {
    if (category === "audio") {
      return section?.audio_data || {};
    } else if (category === "book") {
      return section?.ebook_data || {};
    }
    return {};
  };

  // Get appropriate background image based on device type
  const getBackgroundImage = (section) => {
    return isMobile 
      ? section.mobile_image || "https://www.w3schools.com/w3images/lights.jpg"
      : section.computer_image || "https://www.w3schools.com/w3images/lights.jpg";
  };

  // Updated navigation URL logic to handle series
  const getNavigationUrl = (contentData) => {
    if (category === "audio") {
      return `/audio-play/${contentData.slug}`;
    } else if (category === "book") {
      // Check if series_id exists and is not null
      if (contentData.series_id && contentData.series_id !== null) {
        return `/book-details/${contentData.slug}`;
      }
      return `/book-detail/${contentData.slug}`;
    }
    return "#";
  };

  // Get icon based on content type
  const getActionIcon = () => {
    if (category === "audio") {
      return <Play size={18} color="black" fill="black" />;
    } else if (category === "book") {
      return <BookOpen size={18} color="black" />;
    }
    return null;
  };

  // Get action button text
  const getActionText = () => {
    if (category === "audio") {
      return "Listen";
    } else if (category === "book") {
      return "Read";
    }
    return "View";
  };

  // ADD THIS NEW FUNCTION TO HANDLE LISTEN/PLAY ACTION
  const handlePlayAction = (e, contentData) => {
    e.stopPropagation();
    
    if (category === "audio") {
      // Add continue listening record if authenticated
      if (isAuthenticated) {
        addContinueListening({
          item_type: "audio",
          item_id: contentData.id,
        });
      }

      // Create audio player data
      const audioPlayerData = {
        id: contentData.id,
        title: contentData.title,
        author: contentData.author_name || "Unknown Author",
        song_url: contentData.audio_url,
        image: isMobile 
          ? contentData.mobile_image_url || contentData.computer_image_url
          : contentData.computer_image_url || contentData.mobile_image_url,
        duration: contentData.duration,
        audioId: contentData.id,
        slug: contentData.slug,
      };
      
      // Play the audio
      playAudio(audioPlayerData);
    } else {
      // For non-audio content, navigate to the content
      navigate(getNavigationUrl(contentData));
    }
  };

  // ADD THIS NEW FUNCTION TO HANDLE INFO ACTION
  const handleInfoAction = (e, contentData) => {
    e.stopPropagation();
    navigate(getNavigationUrl(contentData));
  };

  const handleSlideChange = (swiper) => {
    if (heroSections && heroSections.length > 0) {
      setSelectedSection(heroSections[swiper.activeIndex]);
    }
  };

  // Determine which effect to use based on number of images
  const getSwiperEffect = () => {
    return heroSections && heroSections.length > 1 ? "slide" : "fade";
  };

  // Get swiper modules based on effect
  const getSwiperModules = () => {
    const baseModules = [Autoplay];
    if (heroSections && heroSections.length > 1) {
      baseModules.push(Pagination);
    } else {
      baseModules.push(EffectFade);
    }
    return baseModules;
  };

  if (isLoadingHeroSections) {
    return (
      <div className={styles.loadingContainer}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {heroSections && heroSections.length > 0 && (
        <Swiper
          className={styles.heroSwiper}
          modules={getSwiperModules()}
          effect={getSwiperEffect()}
          slidesPerView={1}
          speed={heroSections.length > 1 ? 1000 : 5000}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          pagination={heroSections.length > 1 ? {
            el: `.${styles.swiperPagination}`,
            clickable: true,
            bulletClass: styles.paginationBullet,
            bulletActiveClass: `${styles.paginationBullet} swiper-pagination-bullet-active`,
          } : false}
          onSlideChange={handleSlideChange}
        >
          {heroSections.map((section, index) => {
            const contentData = getContentData(section);
            const navigationUrl = getNavigationUrl(contentData);
            const imageUrl = getBackgroundImage(section);

            return (
              <SwiperSlide key={section.id} className={styles.heroSlide}>
                <div
                  className={styles.heroSection}
                  onClick={() => navigate(navigationUrl)}
                >
                  <img
                    src={imageUrl}
                    alt={contentData.title || "Hero Image"}
                    className={styles.heroImage}
                    loading={index === 0 ? "eager" : "lazy"}
                    fetchpriority={index === 0 ? "high" : "auto"}
                  />
                  <div className={styles.gradient} />
                  <div className={styles.content}>
                    <div className={styles.buttonGroup}>
                      <button
                        className={styles.listenButton}
                        onClick={(e) => handlePlayAction(e, contentData)}
                      >
                        <span className={styles.playIconWrapper}>
                          {getActionIcon()}
                        </span>
                        <span>{getActionText()}</span>
                      </button>
                      <button
                        className={styles.moreButton}
                        onClick={(e) => handleInfoAction(e, contentData)}
                      >
                        <Info />
                        Info
                      </button>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}
      {/* Only show pagination if there are multiple slides */}
      {heroSections && heroSections.length > 1 && (
        <div className={styles.swiperPagination}></div>
      )}
    </div>
  );
};

export default OverlayHome;